import { useState } from 'react';
import type { Order, RefundInfo } from '@shared/types';
import { useAuth } from '../app/auth';
import { api, ApiError, describeError } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { formatDateTime, formatMoney } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Field,
  Input,
  Modal,
  Notice,
  PageHeader,
  Pagination,
  Select,
  Table,
  Textarea,
  type Column,
} from '../components/ui';

const STATUS_META: Record<Order['status'], { label: string; tone: 'ok' | 'warn' | 'neutral' | 'danger' | 'brand' }> = {
  pending: { label: '待支付', tone: 'warn' },
  paid: { label: '已支付', tone: 'ok' },
  failed: { label: '失败', tone: 'danger' },
  refunded: { label: '已退款', tone: 'brand' },
  closed: { label: '已关闭', tone: 'neutral' },
};

interface OrderList {
  items: Order[];
  total: number;
  page: number;
  pageSize: number;
}

interface OrderDetail {
  order: Order;
  refunds: RefundInfo[];
  refundedMinor: number;
  refundableMinor: number;
}

export function OrdersPage() {
  const { can } = useAuth();
  const canRefund = can('order.refund');

  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [query, setQuery] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');
  const [version, setVersion] = useState(0);

  const [detail, setDetail] = useState<OrderDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState<string | null>(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refunding, setRefunding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const orders = useAsync<OrderList>(() => {
    const params = new URLSearchParams({ page: String(page), pageSize: '20' });
    if (status !== '') params.set('status', status);
    if (appliedQuery !== '') params.set('query', appliedQuery);
    return api.get(`/api/orders?${params.toString()}`);
  }, [page, status, appliedQuery, version]);

  async function openDetail(order: Order) {
    setLoadingDetail(order.id);
    setError(null);
    try {
      const data = await api.get<OrderDetail>(`/api/orders/${order.id}`);
      setDetail(data);
      setRefundAmount((data.refundableMinor / 100).toFixed(2));
      setRefundReason('');
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setLoadingDetail(null);
    }
  }

  async function submitRefund() {
    if (!detail) return;
    const amountMinor = Math.round(Number(refundAmount) * 100);
    if (!Number.isFinite(amountMinor) || amountMinor <= 0) {
      setError('退款金额必须大于 0');
      return;
    }

    setRefunding(true);
    setError(null);
    try {
      await api.post(`/api/orders/${detail.order.id}/refund`, {
        amountMinor,
        reason: refundReason.trim() || '后台退款',
      });
      setDetail(null);
      setVersion((value) => value + 1);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : describeError(caught));
    } finally {
      setRefunding(false);
    }
  }

  const columns: Column<Order>[] = [
    {
      key: 'id',
      header: '订单',
      render: (row) => (
        <div>
          <p className="font-mono text-xs text-fg">{row.id}</p>
          <p className="text-xs text-fg-subtle">
            {row.subject ?? row.productCode ?? row.kind} · {row.provider}
          </p>
        </div>
      ),
    },
    {
      key: 'tenant',
      header: '组织',
      render: (row) => (
        <div>
          <p className="text-fg">{row.tenantName || '—'}</p>
          <p className="font-mono text-xs text-fg-subtle">{row.tenantId}</p>
        </div>
      ),
    },
    {
      key: 'amount',
      header: '金额',
      render: (row) => <span className="tabular">{formatMoney(row.amountMinor, row.currency)}</span>,
    },
    {
      key: 'status',
      header: '状态',
      render: (row) => (
        <Badge tone={STATUS_META[row.status].tone}>{STATUS_META[row.status].label}</Badge>
      ),
    },
    {
      key: 'created',
      header: '创建时间',
      render: (row) => <span className="text-xs text-fg-muted">{formatDateTime(row.createdAt)}</span>,
    },
    {
      key: 'actions',
      header: '',
      width: '90px',
      render: (row) => (
        <Button size="sm" loading={loadingDetail === row.id} onClick={() => void openDetail(row)}>
          详情
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="订单与退款"
        description="金额动作全部留痕。部分退款允许多次，但累计不得超过订单金额。"
      />

      {error && detail === null ? <Notice tone="danger">{error}</Notice> : null}

      <Card>
        <CardHeader title="订单" hint={`${orders.data?.total ?? 0} 条`} />
        <div className="mb-3 flex flex-wrap gap-2">
          <div className="w-40">
            <Select
              value={status}
              onChange={(value) => {
                setStatus(value);
                setPage(1);
              }}
              options={[
                { value: '', label: '全部状态' },
                { value: 'pending', label: '待支付' },
                { value: 'paid', label: '已支付' },
                { value: 'refunded', label: '已退款' },
                { value: 'closed', label: '已关闭' },
              ]}
            />
          </div>
          <div className="w-64">
            <Input
              value={query}
              onChange={setQuery}
              placeholder="订单号 / 组织名 / 产品编码"
              onEnter={() => {
                setAppliedQuery(query.trim());
                setPage(1);
              }}
            />
          </div>
          <Button
            onClick={() => {
              setAppliedQuery(query.trim());
              setPage(1);
            }}
          >
            搜索
          </Button>
        </div>

        <Table
          columns={columns}
          rows={orders.data?.items ?? []}
          getKey={(row) => row.id}
          loading={orders.loading}
          empty="没有符合条件的订单"
        />
        <Pagination
          page={page}
          pageSize={orders.data?.pageSize ?? 20}
          total={orders.data?.total ?? 0}
          onChange={setPage}
        />
      </Card>

      <Modal
        open={detail !== null}
        title="订单详情"
        onClose={() => setDetail(null)}
        width="max-w-2xl"
        footer={<Button onClick={() => setDetail(null)}>关闭</Button>}
      >
        {detail ? (
          <div className="space-y-4">
            {error ? <Notice tone="danger">{error}</Notice> : null}

            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[13px]">
              <div className="col-span-2">
                <dt className="text-fg-muted">订单号</dt>
                <dd className="font-mono text-xs text-fg">{detail.order.id}</dd>
              </div>
              <div>
                <dt className="text-fg-muted">组织</dt>
                <dd className="text-fg">{detail.order.tenantName || detail.order.tenantId}</dd>
              </div>
              <div>
                <dt className="text-fg-muted">通道</dt>
                <dd className="text-fg">{detail.order.provider}</dd>
              </div>
              <div>
                <dt className="text-fg-muted">金额</dt>
                <dd className="tabular text-fg">{formatMoney(detail.order.amountMinor)}</dd>
              </div>
              <div>
                <dt className="text-fg-muted">状态</dt>
                <dd>
                  <Badge tone={STATUS_META[detail.order.status].tone}>
                    {STATUS_META[detail.order.status].label}
                  </Badge>
                </dd>
              </div>
              <div>
                <dt className="text-fg-muted">已退</dt>
                <dd className="tabular text-fg">{formatMoney(detail.refundedMinor)}</dd>
              </div>
              <div>
                <dt className="text-fg-muted">可退</dt>
                <dd className="tabular text-fg">{formatMoney(detail.refundableMinor)}</dd>
              </div>
              <div>
                <dt className="text-fg-muted">创建</dt>
                <dd className="text-fg">{formatDateTime(detail.order.createdAt)}</dd>
              </div>
              <div>
                <dt className="text-fg-muted">支付</dt>
                <dd className="text-fg">{formatDateTime(detail.order.paidAt)}</dd>
              </div>
            </dl>

            {detail.refunds.length > 0 ? (
              <div>
                <p className="mb-1 text-[13px] font-medium text-fg">退款记录</p>
                <ul className="space-y-1 text-xs text-fg-muted">
                  {detail.refunds.map((refund) => (
                    <li key={refund.id} className="flex items-center justify-between gap-2">
                      <span className="font-mono">{refund.id}</span>
                      <span className="tabular">{formatMoney(refund.amountMinor)}</span>
                      <Badge tone={refund.status === 'succeeded' ? 'ok' : 'warn'}>
                        {refund.status}
                      </Badge>
                      <span>{formatDateTime(refund.createdAt)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {canRefund && detail.order.status === 'paid' && detail.refundableMinor > 0 ? (
              <div className="space-y-2 border-t border-line pt-3">
                <p className="text-[13px] font-medium text-fg">发起退款</p>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="退款金额（元）">
                    <Input type="number" value={refundAmount} onChange={setRefundAmount} />
                  </Field>
                  <Field label="原因">
                    <Textarea
                      value={refundReason}
                      onChange={setRefundReason}
                      rows={1}
                      placeholder="客户申请 / 服务不可用"
                    />
                  </Field>
                </div>
                <Button variant="danger" loading={refunding} onClick={() => void submitRefund()}>
                  确认退款
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
